import { makeAutoObservable } from "mobx";
import * as AWS from "aws-sdk";
// import contentDisposition from 'content-disposition';
import {
  API_BASE_URL,
  CLOUDFRONT_BASE_URL,
  DISABLE_CACHEING,
} from "../../../SearchState";
import { globalAudioContext } from "../../MusicTrack/MusicTrackState";
import UploadTrackState from "../UploadTrackState";
import retryFetch from "../../../utils/retryFetch";

/** @typedef {import("@shared/musicEndpointTypes").AutocompletePost} AutocompletePost */
/** @typedef {import("@shared/musicEndpointTypes").AutocompletePostResponse} AutocompletePostResponse */
/** @typedef {import("@shared/musicEndpointTypes").TrackPost} TrackPost */
/** @typedef {import("@shared/musicEndpointTypes").TrackPostResponse} TrackPostResponse */
/** @typedef {import("@shared/musicEndpointTypes").TrackExistsPost} TrackExistsPost */
/** @typedef {import("@shared/musicEndpointTypes").TrackExistsPostResponse} TrackExistsPostResponse */
/** @typedef {import("@shared/musicEndpointTypes").CloudfrontInvalidatePost} CloudfrontInvalidatePost */
/** @typedef {import("@shared/musicEndpointTypes").CloudfrontInvalidatePostResponse} CloudfrontInvalidatePostResponse */
/** @typedef {import("@shared/typedefs").Category} Category */
/** @typedef {import("@shared/authEndpointDatatypes").S3AuthGetResponse} S3AuthGetResponse */
/** @typedef {import("../../../../sql/schemaDatatypes").MusicSql} MusicSql */

export default class TrackToUploadState {
  /** @type {UploadTrackState} */
  uploadTrackState;

  /** @type {string} */
  trackName;

  /** @type {File | null} */
  mp3File;

  mp3Duration = 0;

  mp3UploadProgress = 0;

  /** @type {File | null} */
  wavFile;

  wavDuration = 0;

  wavUploadProgress = 0;

  uploading = false;

  isReplacing = false;

  /**
   * @param {UploadTrackState} uploadTrackState
   */
  constructor(uploadTrackState) {
    makeAutoObservable(this, {}, { enforceActions: "observed" });
    this.uploadTrackState = uploadTrackState;
    this.setTrackName();
  }

  setTrackName = () => {
    this.trackName = this.uploadTrackState.name
      .replace(".mp3", "")
      .replace(".wav", "");
  };

  /**
   * @param {File} mp3
   * @action
   */
  setMp3 = (mp3) => {
    this.mp3File = mp3;
    const fileReader = new FileReader();
    fileReader.onload = (event) => {
      globalAudioContext.decodeAudioData(event.target.result, (buffer) => {
        this.mp3Duration = Math.round(buffer.duration);
      });
    };
    fileReader.readAsArrayBuffer(mp3);
    /** @type {string} */
    const name = this.mp3File.name.replace(".mp3", "");
    this.checkIfExists(name);
  };

  /**
   * @param {File} wav
   * @action
   */
  setWav = (wav) => {
    this.wavFile = wav;
    const fileReader = new FileReader();
    fileReader.onload = (event) => {
      globalAudioContext.decodeAudioData(event.target.result, (buffer) => {
        this.wavDuration = Math.round(buffer.duration);
      });
    };
    fileReader.readAsArrayBuffer(wav);
    /** @type {string} */
    const name = this.wavFile.name.replace(".wav", "");
    this.checkIfExists(name);
  };

  checkIfExists = (name) => {
    /** @type {TrackExistsPost} */
    const savedPlaylist = {
      query: name,
    };
    return retryFetch(`${API_BASE_URL}/api/v1/checkiftrackexists`, {
      method: "POST",
      cache: DISABLE_CACHEING ? "reload" : "default",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify(savedPlaylist),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.length > 0) {
          if (r[0].name === name) {
            this.isReplacing = true;
          }
        }
      });
  };

  reset = () => {
    this.uploading = false;
    this.mp3UploadProgress = 0;
    this.wavUploadProgress = 0;
    this.uploadTrackState.close();
  };

  remove = () => {
    this.uploadTrackState.remove(this);
  };

  /**
   * Uploads the track files to S3 and creates database entry
   * @param {Category} category
   * @returns {Promise<TrackPostResponse>}
   * @action
   */
  upload = (category) => {
    const mp3File = this.mp3File;
    const wavFile = this.wavFile;
    if (mp3File == null || wavFile == null) {
      return Promise.resolve({ success: false });
    }

    /** @type {string} */
    const name = mp3File.name.replace(".mp3", "").replace("+", " ");
    console.log(`mp3 file name: ${mp3File.name}`);

    /** @type {AutocompletePost} */
    const savedPlaylist = {
      query: name,
    };
    return retryFetch(`${API_BASE_URL}/api/v1/autocomplete`, {
      method: "POST",
      cache: DISABLE_CACHEING ? "reload" : "default",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify(savedPlaylist),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((r) =>
        // if (r.filter(track => track === name).length > 0) {
        //   alert(
        //     `Not uploading ${name} because it already exists! Please delete it before uploading again.`
        //   );
        //   this.mp3UploadProgress = 100;
        //   this.wavUploadProgress = 100;
        //   return Promise.resolve({ success: false });
        // }
        // console.log('testing reaplcement')
        // return;

        retryFetch(`${API_BASE_URL}/api/v1/auth/s3_credentials`, {
          method: "GET",
          redirect: "error",
          withCredentials: true,
          credentials: "include",
          cache: DISABLE_CACHEING ? "reload" : "default",
        })
          .then((r) => r.json())
          .then((r) => {
            if (!r.authorized) {
              alert("You are not authorized to upload");
              return Promise.resolve();
            }

            const mp3Path = `mp3/${category.category_name}/${name}.mp3`;
            const wavPath = `wav/${name}.wav`;

            // Set up S3

            AWS.config.update({
              region: r.bucketRegion,
              credentials: new AWS.CognitoIdentityCredentials({
                IdentityPoolId: r.identityPoolId,
              }),
            });

            const s3 = new AWS.S3({
              apiVersion: "2006-03-01",
              params: { Bucket: r.bucketName },
            });

            // Upload the mp3

            this.uploading = true;
            /**
             * @param {File} track
             * @param {string} path
             * @param {string} contentName
             * @param {function(number): void} progressCallback
             */
            const uploadTrackToS3 = (
              track,
              path,
              contentName,
              progressCallback,
            ) => {
              const upload = s3.upload(
                {
                  Key: path,
                  Body: track,
                  ACL: "public-read",
                  // Metadata: {
                  //   'Content-Disposition': contentName
                  // },
                  ContentDisposition: contentName,
                },
                (err) => {
                  if (err) {
                    console.error(err);
                    alert(
                      `There was an error uploading your track: ${err.message}`,
                    );
                  }
                },
              );
              upload.on("httpUploadProgress", (evt) => {
                progressCallback(evt.loaded / evt.total);
              });
              return upload.promise();
            };

            const mp3CorrectName = `attachment; filename="Asche & Spencer - ${name}.mp3"`;
            const wavCorrectName = `attachment; filename="Asche & Spencer - ${name}.wav"`;
            // const mp3ContentDisposition = contentDisposition(
            //   mp3CorrectName
            // );
            // const wavContentDisposition = contentDisposition(
            //   wavCorrectName
            // );

            return Promise.all([
              uploadTrackToS3(mp3File, mp3Path, mp3CorrectName, (progress) => {
                this.mp3UploadProgress = progress;
              }),
              uploadTrackToS3(wavFile, wavPath, wavCorrectName, (progress) => {
                this.wavUploadProgress = progress;
              }),
            ]).then(() => {
              console.log(
                `Successfully uploaded mp3 track to ${CLOUDFRONT_BASE_URL}/${mp3Path} and wav track to ${CLOUDFRONT_BASE_URL}/${wavPath}`,
              );
              console.log(this.isReplacing);

              if (this.isReplacing === true) {
                console.log("is replacing");
                this.uploading = false;
                /** @type {CloudfrontInvalidatePost} */
                const cloudfrontPaths = {
                  mp3Path: `/${mp3Path.replace(new RegExp(" ", "g"), "+")}`,
                  wavPath: `/${wavPath.replace(new RegExp(" ", "g"), "+")}`,
                };
                console.log("testing invalidation");
                return retryFetch(
                  `${API_BASE_URL}/api/v1/cloudfrontinvalidate`,
                  {
                    method: "POST",
                    headers: new Headers({
                      "content-type": "application/json",
                    }),
                    body: JSON.stringify(cloudfrontPaths),
                    mode: "cors",
                    withCredentials: true,
                    credentials: "include",
                  },
                )
                  .then((res) => res.json())
                  .then((res) => {
                    console.log(res);
                    this.uploading = false;
                    
                    // Resubmit the replaced track to AIMS (fire-and-forget)
                    retryFetch(`${API_BASE_URL}/api/v1/ai/resubmit-track`, {
                      method: "POST",
                      headers: new Headers({ "content-type": "application/json" }),
                      body: JSON.stringify({ trackName: name }),
                      mode: "cors",
                      withCredentials: true,
                      credentials: "include",
                    }).catch((err) => console.error("AIMS resubmit failed:", err));
                    
                    const replaceResponse = { success: true };
                    return replaceResponse;
                  });
              }

              if (this.isReplacing === false) {
                const IsVersionPattern = /[a-z]$/;
                const isVersionMatches = name.match(IsVersionPattern);
                const isVersion = isVersionMatches ? 1 : 0;

                const pattern = /(.*[0-9+])[a-z]?/;
                const matchGroup = name.match(pattern);
                const siblingGroup =
                  matchGroup != null && matchGroup.length > 1
                    ? matchGroup[1]
                    : "";

                console.log(name);

                /** @type {MusicSql} */
                const newTrackSql = {
                  name,
                  // Location of the audio file
                  filename: `${name}.mp3`,
                  url_name: `${name.replace(" ", "-")}.mp3`,
                  file_path_high: `music/${category.category_name}/${name}.mp3`,
                  folder: category.cover,
                  category: category.id,
                  // Description of the audio file
                  duration: this.mp3Duration,
                  waveforms: "",
                  notes: "",
                  // Licensing details
                  licensed: false,
                  exclusive: false,
                  // Alternate version details
                  has_versions: false,
                  is_version: isVersion,
                  version_search_term: siblingGroup,
                };
                /** @type {TrackPost} */
                const trackPost = {
                  musicSql: newTrackSql,
                };

                return retryFetch(`${API_BASE_URL}/api/v1/track`, {
                  method: "POST",
                  headers: new Headers({
                    "content-type": "application/json",
                  }),
                  body: JSON.stringify(trackPost),
                  mode: "cors",
                  withCredentials: true,
                  credentials: "include",
                })
                  .then((res) => res.json())
                  .then((res) => {
                    console.log("successfully posted track to SQL");
                    // this.mp3UploadProgress = 0;
                    // this.wavUploadProgress = 0;
                    this.uploading = false;
                    return res;
                  });
              }
            });
          }),
      );
  };
}
